import { useMemo } from 'react';
import useMeQuery from '@hooks/useMeQuery';
import { getAttendeeVisibility } from '@tools/getAttendeeVisibility';
import resolveEnvironment from '@tools/resolveEnvironment';
import getConfig from 'next/config';
import { usePageLoadMetrics } from '@cvent/sli-nextjs-metrics';
import { getBrowserProperties, getDeviceProperties } from '../analytics/baseProperties';
import { appVariables, Product } from '../apollo/appVariables';

const { publicRuntimeConfig } = getConfig();

const appVersion = publicRuntimeConfig.CLIENT_VERSION;

/**
 * Hook to set the Metrics' base properties to send with each event. It checks if the user
 * is logged in so it can also be used for non authenticated pages like the /landing page and
 * registration widget. The Me query is only executed if the user is logged in to get the visibility,
 * event info etc.
 *
 * @param contactId
 * @param accountMappingId
 * @param locale
 * @returns
 */
export const useMetricsBaseProps = (
  contactId: string,
  accountMappingId: string,
  locale: string
): Record<string, unknown> => {
  const { eventId, attendeeId, publicPage, isWebinar } = appVariables();
  const {
    loading,
    data: {
      me: {
        guest = undefined,
        visibility: meVisibility = undefined,
        virtual: isVirtualRegType = undefined,
        event: { title, appConfig, format } = { title: undefined, appConfig: undefined, format: undefined }
      } = {}
    } = {}
  } = useMeQuery();

  const visibility = getAttendeeVisibility(meVisibility, appConfig?.webConfig?.defaultProfileVisibility);

  const { updateMetricData } = usePageLoadMetrics();

  if (isVirtualRegType !== undefined && format !== undefined) {
    updateMetricData({ isVirtualRegType, eventType: format });
  }

  const baseProperties = useMemo(() => {
    if ((eventId && contactId && attendeeId && accountMappingId && locale && !loading) || publicPage) {
      return {
        ...getBrowserProperties(),
        ...getDeviceProperties(),
        appVersion,
        platform: publicRuntimeConfig.ANALYTICS_PLATFORM,
        environment: resolveEnvironment(),
        eventId,
        contactId,
        attendeeId,
        accountMappingId,
        locale,
        eventName: title,
        guest,
        visibility,
        isVirtualRegType,
        eventType: format,
        product: isWebinar ? Product.WEBINAR : Product.ATTENDEE_HUB
      };
    }
    return null;
  }, [
    eventId,
    contactId,
    attendeeId,
    accountMappingId,
    locale,
    title,
    guest,
    visibility,
    isWebinar,
    loading,
    publicPage,
    format,
    isVirtualRegType
  ]);

  return baseProperties;
};
